import { formatNumberToK } from 'module/helpers/formatNumberToK';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb/breadcrumb';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { IconShell } from 'module/ui/components/atoms/icon-shell';
import { BodyLong01 } from 'module/ui/components/atoms/ozone/atoms/typography/body';
import { Popover as PopoverAtom } from 'module/ui/components/atoms/popover';
import { RelativeTime } from 'module/ui/components/atoms/relative-time';
import { Tag } from 'module/ui/components/atoms/tag';
import {
  Label01,
  HelperText01,
  HeadingCompact01,
  Heading03,
} from 'module/ui/components/styles';
//import { Popover } from 'antd';
import {
  faArrowUpRightFromSquare,
  faClose,
} from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { Skeleton } from 'antd';
import { CitationType } from 'api/citation/types';
import { CompanyKanbanItem, SignalOccurrence } from 'api/company/types';
import { SignalCitation, SignalType } from 'api/signal/types';
import { SignalField } from 'api/timeline/types';
import moment from 'moment';
import { useSignalOccurrenceQuery } from 'queries/company';
import React, { useState, useMemo, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactMarkdown, { Components } from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import { preprocessMessageWithCitations } from 'utils/citation-preprocessor';
import { FlexWrapperWithGutter } from 'components/common/styles';
import { CitationDataPopover } from 'components/organisms/document-editor/extensions/citation/ui/citation-data-popover';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';
import { TimelineSignalCard } from 'components/organisms/signal-output/timeline-signal-card';
import { SignalOccurenceCard } from './signal-occurence-card';

interface SignalCardProps {
  signalName: string;
  signalItem: CompanyKanbanItem;
  signalType: SignalType;
  isModuleFederation?: boolean;
}

const FieldNameText = styled.p`
  ${HelperText01};
  color: var(--text-tertiary);
`;

const ReasonLabel = styled.p`
  ${Label01};
  color: var(--text-primary);
  margin-bottom: var(--spacing-02);
`;

const PopoverHeader = styled.p`
  ${HeadingCompact01};
  color: var(--text-primary);
  border-bottom: 1px solid var(--field-border-default);
  margin-bottom: var(--spacing-04);
  padding-bottom: var(--spacing-03);
`;
const CardContainer = styled.div`
  padding: var(--spacing-05);
  width: 300px;
  margin-bottom: var(--spacing-03);
  background-color: var(--white-100);
  cursor: pointer;
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }
`;
const CardTitleLink = styled(Link)`
  ${HeadingCompact01};
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  display: block;
  &:hover {
    color: var(--link-color);
  }
`;
const ChurnActiveText = styled.p<{ signalType: SignalType }>`
  margin: 0 0 12px 0;
  color: ${(props) => signalTypeConfigs[props.signalType].colorVariable};
`;

const Popover = styled(PopoverAtom)`
  .ant-popover-inner {
    padding: var(--spacing-04);
  }
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
`;

const SummaryLabel = styled.div`
  ${Label01};
  color: var(--black-100);
`;

const FullSummaryText = styled.div`
  ${BodyLong01};
  color: var(--text-primary);
`;

const SummaryText = styled.div`
  ${HelperText01};
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`;

const ReasonsWrapper = styled.div<{ hasBottomBorder?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: var(--spacing-04);
  padding-bottom: var(--spacing-04);
  border-bottom: ${(props) =>
    props.hasBottomBorder ? '1px solid var(--field-border-default)' : 'none'};
`;
const PastChurnText = styled.p`
  ${HelperText01};
  color: var(--text-secondary);
`;
const ActivityBarContainer = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: var(--spacing-03);
  width: 60%;
`;
const ActivityBarDay = styled.div<{ signalType?: SignalType }>`
  height: 12px;
  width: 12px;
  background-color: ${({ signalType }) =>
    signalType
      ? signalTypeConfigs[signalType].colorVariable
      : 'var(--scarlet-gray-50)'};
  transition: background-color 0.2s ease;
`;

const PopoverCard = styled.div`
  min-width: 200px;
  max-width: 270px;
  max-height: 300px;
  display: flex;
  overflow-y: auto;
  background-color: var(--white-100);
`;
const PopoverUI = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--spacing-01);
`;
const TimelineSignalCardWrapper = styled(TimelineSignalCard)`
  padding-left: var(--spacing-10);
  padding-right: var(--spacing-10);
`;

const SignalOccurrencesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-07);
`;

const groupReasons = (reasons: string[]) => {
  const reasonCounts: Record<string, number> = {};

  // Count occurrences of each reason
  for (const reason of reasons) {
    const normalizedReason = reason.trim().toLowerCase();

    // Find existing reason that matches (case-insensitive)
    const existingReason = Object.keys(reasonCounts).find(
      (key) => key.toLowerCase() === normalizedReason,
    );

    if (existingReason) {
      reasonCounts[existingReason]++;
    } else {
      reasonCounts[reason] = 1;
    }
  }

  // Convert to array format
  return Object.keys(reasonCounts)
    .map((reason) => ({
      reason,
      count: reasonCounts[reason],
    }))
    .sort((a, b) => b.count - a.count);
};

const FlexWrapperWithGutterAndWrap = styled(FlexWrapperWithGutter)`
  flex-wrap: wrap;
`;
const ReasonCount = styled.div<{ signalType: SignalType }>`
  opacity: var(--black-with-opacity);
  width: 22px;
  height: 22px;
  border-radius: var(--border-radius-large);
  padding: var(--spacing-01) var(--spacing-03);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => {
    if (props.signalType === SignalType.Opportunity) {
      return 'var(--green-70)';
    }
    if (props.signalType === SignalType.Risk) {
      return 'var(--red-70)';
    }

    return 'var(--gray-60)';
  }};
  background-color: ${(props) => {
    if (props.signalType === SignalType.Opportunity) {
      return 'var(--green-30)';
    }
    if (props.signalType === SignalType.Risk) {
      return 'var(--red-30)';
    }

    return 'var(--gray-30)';
  }};
  margin-left: var(--spacing-02);
`;

function PopoverReasons({
  reasons,
  signalType,
}: {
  reasons: { reason: string; count: number }[];
  signalName: string;
  signalType: SignalType;
}) {
  return (
    <PopoverUI>
      <PopoverHeader>
        <FormattedMessage id="reason" />
      </PopoverHeader>
      <ReasonsWrapper>
        {reasons.map((reason, index) => (
          <Tag
            key={`${reason}-${index}`}
            color={signalTypeConfigs[signalType].color}
          >
            <span>{reason.reason}</span>
            {reason.count > 1 && (
              <ReasonCount signalType={signalType}>{reason.count}</ReasonCount>
            )}
          </Tag>
        ))}
      </ReasonsWrapper>
    </PopoverUI>
  );
}

const createMarkdownComponents = (
  citationsMapping: SignalCitation,
): Partial<Components> => ({
  cite: (props: any) => {
    const citationNum = props['data-num'];

    return (
      <StyledCitationPopover
        label={citationNum}
        citationId={citationsMapping?.[citationNum]?.citationUuid ?? 0}
        citationType={
          citationsMapping?.[citationNum]?.citationType as CitationType
        }
      />
    );
  },
});

// Memoized markdown component to prevent unnecessary re-renders
const MemoizedReactMarkdown = memo<{
  children: string;
  citationsMapping?: SignalCitation;
}>(({ children, citationsMapping }) => {
  const markdownComponents = useMemo(
    () => createMarkdownComponents(citationsMapping ?? {}),
    [citationsMapping],
  );

  const processedContent = useMemo(
    () => preprocessMessageWithCitations(children),
    [children],
  );

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeRaw]]}
      components={markdownComponents}
    >
      {processedContent}
    </ReactMarkdown>
  );
});

MemoizedReactMarkdown.displayName = 'MemoizedReactMarkdown';

function FullSummaryPopover({
  summary,
  citationsMapping,
}: {
  summary: string;
  citationsMapping?: SignalCitation;
}) {
  return (
    <PopoverUI>
      <PopoverHeader>
        <FormattedMessage id="summary" />
      </PopoverHeader>
      <FullSummaryText>
        <MemoizedReactMarkdown citationsMapping={citationsMapping}>
          {summary}
        </MemoizedReactMarkdown>
      </FullSummaryText>
    </PopoverUI>
  );
}
const DrawerHeaderTitle = styled.div`
  ${Heading03};
`;
const getActivityText = function (signal: string) {
  return `${signal} active since `;
};

const StyledCitationPopover = styled(CitationDataPopover)`
  display: inline-block;
`;
const AccountsSignalCard: React.FC<SignalCardProps> = ({
  signalItem,
  signalName,
  signalType,
  isModuleFederation = false,
}) => {
  const {
    latestSignalOccurrenceTime,
    weeklySignalOccurrentCounts,
    reasons,
    companyName,
    companyARR,
    companyId,
    signalOccurrences: signalOccurrencesIds,
    latestSignalOccurrence,
  } = signalItem;

  const {
    data: signalOccurrences,
    refetch,
    isLoading,
  } = useSignalOccurrenceQuery({
    companyId,
    configId: 1,
    signalOccurrenceId: signalOccurrencesIds.join(','),
    enabled: false,
  });

  const [isSignalDrawerVisible, setIsSignalDrawerVisible] = useState(false);

  const groupedByWeek = signalOccurrences?.reduce(
    (acc, signal) => {
      const weekStart = moment(signal.meetingTime)
        .startOf('week')
        .format('YYYY-MM-DD');

      if (!acc[weekStart]) {
        acc[weekStart] = [];
      }
      acc[weekStart].push(signal);

      return acc;
    },
    {} as Record<string, SignalOccurrence[]>,
  );

  const momentLatestSignalOccurrenceTime = moment(latestSignalOccurrenceTime);
  const signalOccurenceCount = weeklySignalOccurrentCounts.reduce(
    (acc, curr) => acc + curr,
    0,
  );

  const navigate = useNavigate();

  const signalsV2SummaryPath = useMemo(() => {
    const accountIdSegment = companyId.toString();

    if (isModuleFederation) {
      return `/${ROUTE_PATHS.ACCOUNTS}/${accountIdSegment}/${ROUTE_PATHS.SIGNALS_V2}`;
    }

    return `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(':accountId', accountIdSegment)}/${ROUTE_PATHS.SIGNALS_V2}`;
  }, [companyId, isModuleFederation]);

  const goToTimelineView = (companyId: number, isOpenInNewTab = false) => {
    const accountIdSegment = companyId.toString();

    if (isModuleFederation) {
      const signalsV2Path = `/${ROUTE_PATHS.ACCOUNTS}/${accountIdSegment}/${ROUTE_PATHS.SIGNALS_V2}?view=timeline`;

      if (isOpenInNewTab) {
        window.open(signalsV2Path, '_blank');
      } else {
        navigate(signalsV2Path);
      }
    } else if (isOpenInNewTab) {
      window.open(
        `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(':accountId', accountIdSegment)}/${ROUTE_PATHS.ACCOUNT_DETAIL_TIMELINE}`,
        '_blank',
      );
    } else {
      navigate(
        `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(':accountId', accountIdSegment)}/${ROUTE_PATHS.ACCOUNT_DETAIL_TIMELINE}`,
      );
    }
  };
  const handleOpenSignalDrawer = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (signalOccurrences === undefined) {
      refetch();
    }
    setIsSignalDrawerVisible(true);
  };

  const groupedReasons = reasons.length > 0 ? groupReasons(reasons) : [];

  return (
    <CardContainer onClick={(e) => handleOpenSignalDrawer(e)}>
      <CardTitleLink
        to={signalsV2SummaryPath}
        onClick={(e) => e.stopPropagation()}
      >
        {companyName}
      </CardTitleLink>
      <FieldNameText>
        ARR: {companyARR ? formatNumberToK(companyARR) : '--'}
      </FieldNameText>
      <ChurnActiveText signalType={signalType}>
        <span>
          {getActivityText(signalName)}
          <RelativeTime time={momentLatestSignalOccurrenceTime} />
        </span>
      </ChurnActiveText>
      {reasons.length > 0 && (
        <>
          <ReasonLabel>
            <FormattedMessage id="reason" />
          </ReasonLabel>
          <ReasonsWrapper hasBottomBorder>
            <Popover
              content={
                groupedReasons.length > 1 && (
                  <PopoverCard>
                    <PopoverReasons
                      reasons={groupedReasons.slice(1)}
                      signalName={signalName}
                      signalType={signalType}
                    />
                  </PopoverCard>
                )
              }
              mouseEnterDelay={0.3}
              autoAdjustOverflow
              destroyTooltipOnHide
              placement="right"
            >
              <FlexWrapperWithGutterAndWrap>
                {groupedReasons.slice(0, 1).map((reason, index) => (
                  <Tag color={signalTypeConfigs[signalType].color} key={index}>
                    <span>{reason.reason}</span>
                    {reason.count > 1 ? (
                      <ReasonCount signalType={signalType}>
                        {reason.count}
                      </ReasonCount>
                    ) : null}
                  </Tag>
                ))}
                {groupedReasons.length > 1 && (
                  <Tag color={signalTypeConfigs[signalType].color}>
                    +{groupedReasons.length - 1}
                  </Tag>
                )}
              </FlexWrapperWithGutterAndWrap>
            </Popover>
          </ReasonsWrapper>
        </>
      )}
      <Popover
        content={
          <PopoverCard>
            {isLoading ? (
              <CardContainer>
                <Skeleton />
              </CardContainer>
            ) : (
              <SignalOccurenceCard
                signalOccurrence={groupedByWeek}
                signalType={signalType}
              />
            )}
          </PopoverCard>
        }
        placement="right"
        onOpenChange={(open) => {
          if (open) {
            // Refetch when popover opens
            refetch();
          }
        }}
      >
        <ActivityBarContainer>
          {Array.from(weeklySignalOccurrentCounts)
            .reverse()
            .map((signalCount, index) => {
              return (
                <ActivityBarDay
                  key={index}
                  signalType={signalCount > 0 ? signalType : undefined}
                />
              );
            })}
        </ActivityBarContainer>
      </Popover>

      <PastChurnText>
        {signalOccurenceCount} {signalName} in the past 12 weeks
      </PastChurnText>
      <SummaryContainer>
        <SummaryLabel>Summary</SummaryLabel>
        <Popover
          content={
            <PopoverCard>
              <FullSummaryPopover
                summary={latestSignalOccurrence?.summary ?? ''}
                citationsMapping={latestSignalOccurrence?.citationMapping}
              />
            </PopoverCard>
          }
          placement="right"
          mouseEnterDelay={0.3}
          autoAdjustOverflow
          destroyTooltipOnHide
        >
          <SummaryText>
            <MemoizedReactMarkdown
              citationsMapping={latestSignalOccurrence?.citationMapping}
            >
              {latestSignalOccurrence?.summary ?? ''}
            </MemoizedReactMarkdown>
          </SummaryText>
        </Popover>
      </SummaryContainer>

      <CustomLayoutDrawer
        open={isSignalDrawerVisible}
        onClose={(e) => {
          e.stopPropagation();
          setIsSignalDrawerVisible(false);
        }}
        width={690}
        {...DefaultCustomLayoutDrawerProps}
        closeIcon={<></>}
        closable={false}
        maskClosable={false}
      >
        <CustomLayoutDrawerContent>
          <CustomLayoutDrawerContent.Title>
            <div className="flex flex-row justify-between">
              <div className="flex flex-col grow justify-between">
                <Breadcrumb>
                  <DrawerBreadCrumbs breadcrumbs={[{ title: companyName }]} />
                </Breadcrumb>
                <DrawerHeaderTitle>{signalName}</DrawerHeaderTitle>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <Button
                  value="secondary"
                  className="self-center"
                  onClick={() => goToTimelineView(companyId, true)}
                >
                  <IconShell
                    size="small"
                    icon={
                      <FontAwesomeIcon
                        icon={faArrowUpRightFromSquare}
                        size="sm"
                      />
                    }
                  />
                  <FormattedMessage id="accounts.signal.card.gototimelineview" />
                </Button>
                <Button
                  variant="ghost"
                  className="cursor-pointer w-[30px] h-[30px] flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsSignalDrawerVisible(false);
                  }}
                >
                  <FontAwesomeIcon icon={faClose} className="cursor-pointer" />
                </Button>
              </div>
            </div>
          </CustomLayoutDrawerContent.Title>
          <CustomLayoutDrawerContent.Content>
            {signalOccurrences ? (
              <SignalOccurrencesList>
                {signalOccurrences?.map((signal) => {
                  const reason = signal?.fields?.find(
                    (field) =>
                      Object.keys(signal?.metaData)[0] ===
                      String(field?.fieldId),
                  ) as SignalField;

                  return (
                    <TimelineSignalCardWrapper
                      key={signal?.signalOccurrenceId}
                      people={signal?.reportedBy ?? []}
                      reason={reason}
                      title={signal?.title ?? ''}
                      citations={signal?.citationMapping}
                      description={signal?.summary ?? ''}
                      signalType={signalType}
                      signalName={signalName}
                      createdAt={
                        signal?.createdAt
                          ? new Date(signal.createdAt)
                          : undefined
                      }
                      className="timeline-details-signal"
                    />
                  );
                })}
              </SignalOccurrencesList>
            ) : (
              <>
                <Skeleton active />
                <Skeleton active />
              </>
            )}
          </CustomLayoutDrawerContent.Content>
          <CustomLayoutDrawerContent.Footer $rightAlignedAction>
            <Button
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                setIsSignalDrawerVisible(false);
              }}
            >
              Done
            </Button>
          </CustomLayoutDrawerContent.Footer>
        </CustomLayoutDrawerContent>
      </CustomLayoutDrawer>
    </CardContainer>
  );
};

export { AccountsSignalCard };
